import { DateTime } from 'luxon';
import { ExpressionType } from '../../IExpression.ts';
import { createExpressionType } from '../../IExpressionType.ts';
import { ComputedDateExpressionSchema } from './ComputedDateExpressionSchema.ts';
import {
  ComputedDateOffsetUnit,
  type IComputedDateExpression,
} from './IComputedDateExpression.ts';

export const ComputedDateExpressionType =
  createExpressionType<IComputedDateExpression>({
    type: ExpressionType.ComputedDate,
    schema: ComputedDateExpressionSchema,
    evaluate(expression, scope) {
      const fromValue = scope.evaluate(expression.from);

      if (typeof fromValue !== 'string') {
        return null;
      }

      let current: DateTime | null = null;
      const formats = [
        'yyyy-MM-dd',
        'dd/MM/yyyy',
        'MM/dd/yyyy',
        "yyyy-MM-dd'T'HH:mm:ss",
      ];

      // Parsiamo la data di partenza usando diversi formati
      for (const format of formats) {
        const dt = DateTime.fromFormat(fromValue, format);
        if (dt.isValid) {
          current = dt;
          break;
        }
      }

      if (!current) {
        return null;
      }

      for (const offset of expression.offsets) {
        const amountValue = scope.evaluate(offset.amount);
        const amountNumber = Number(amountValue) || 0;

        switch (offset.unit) {
          case ComputedDateOffsetUnit.Days:
            current = current.plus({ days: amountNumber });
            break;
          case ComputedDateOffsetUnit.Months:
            current = current.plus({ months: amountNumber });
            break;
          case ComputedDateOffsetUnit.Years:
            current = current.plus({ years: amountNumber });
            break;
        }
      }

      return current.toFormat('yyyy-MM-dd');
    },
    getReferences(expression, collect) {
      return [
        expression.from,
        ...expression.offsets.map(o => o.amount),
      ].flatMap(collect);
    },
    toString(expression, toString) {
      let fromLabel: string;
      if (expression.from.type === ExpressionType.Value) {
        if (!expression.from.value) return '';

        fromLabel = DateTime.fromFormat(
          expression.from.value.toString(),
          'yyyy-MM-dd',
        ).toFormat('dd/MM/yyyy');
      } else {
        fromLabel = toString(expression.from);
      }

      const offsetsLabels = expression.offsets.map(o =>
        formatOffsetLabel(o, toString(o.amount)),
      );

      return [fromLabel, ...offsetsLabels].filter(Boolean).join(' ');
    },
  });

/**
 * Genera la label testuale di un offset data da usare nella rappresentazione
 * dell'espressione (es: "+ 3 giorni", "-2 mesi").
 *
 * La funzione conserva i fallback storici:
 * - usa amountLabel come testo principale;
 * - se l'amount literal è presente ma non numerico, mostra "0";
 * - aggiunge il prefisso "+" solo per valori numerici positivi.
 */
function formatOffsetLabel(
  offset: IComputedDateExpression['offsets'][number],
  amountLabel: string,
): string {
  const literalValue =
    'value' in offset.amount ? offset.amount.value : undefined;
  const hasLiteralValue =
    literalValue !== undefined && literalValue !== null && literalValue !== '';

  // Il numero serve solo a capire segno e validità del valore literal.
  const amountNumber = hasLiteralValue ? Number(literalValue) : Number.NaN;

  // Verifica se la label è null, undefined o la stringa 'null'.
  const isInvalidLabel =
    amountLabel === null || amountLabel === undefined || amountLabel === 'null';

  // Verifica se esiste un valore literal ma non è un numero finito.
  const isInvalidNumericLiteral =
    hasLiteralValue && !Number.isFinite(amountNumber);

  // Se la label o il literal numerico sono invalidi, mostriamo "0" (comportamento attuale).
  const displayAmount =
    isInvalidLabel || isInvalidNumericLiteral ? '0' : amountLabel;

  // Prefisso "+" solo per offset numerici positivi.
  const signPrefix =
    Number.isFinite(amountNumber) && amountNumber > 0 ? '+ ' : '';

  return `${signPrefix}${displayAmount} ${mapUnitToLabel(offset.unit)}`;
}

function mapUnitToLabel(unit: ComputedDateOffsetUnit): string {
  switch (unit) {
    case ComputedDateOffsetUnit.Days:
      return 'giorni';
    case ComputedDateOffsetUnit.Months:
      return 'mesi';
    case ComputedDateOffsetUnit.Years:
      return 'anni';
  }
}
